import * as THREE from 'three';

/**
 * Whether the wall is still on screen.
 *
 * Kept apart from the scene because it is arithmetic on a box and a camera,
 * with no renderer in it — which is also the only reason it can be tested.
 *
 * There are three separate ways to lose sight of a wall, and they need three
 * separate measures because no one number tells them apart. A wall panned off
 * the edge is gone but full size. A wall seen edge-on is centred and gone. A
 * wall zoomed out to a speck is centred, square on and gone. Worse, the last
 * two look identical to any measure of how much screen the wall takes up: a
 * backlit wall stood on edge is a 42 mm deep slab, which projects about as
 * much area as the same wall viewed square on from the far end of the zoom
 * range. Only the angle separates them.
 *
 * All of this is measured on the wall's front face rather than its bounding
 * box. The box is the wrong thing to ask about: its depth is frame and air
 * gap, none of which is the design, and counting it is what made an edge-on
 * wall read as comfortably visible.
 */

export interface Framing {
  /**
   * Share of the wall's front face that falls inside the viewport: how much of
   * the design is on screen at all.
   */
  visible: number;
  /**
   * Share of the viewport the front face takes up. Catches a wall too small to
   * read, which the other two measures call perfectly fine.
   */
  coverage: number;
  /**
   * How square-on the wall is: the cosine of the angle between the view and
   * the wall's normal, so 1 is face on and 0 is edge on. Independent of both
   * zoom and panel depth, which is what makes it able to say what neither of
   * the others can.
   */
  facing: number;
}

/**
 * Each pair is deliberately apart. A single threshold chatters while you drag
 * across it, so a lost view has to improve past the point it was lost at
 * before it counts as recovered.
 *
 * The angles are generous on purpose. Looking along a relief is how its depth
 * gets judged, so the whole ordinary range stays quiet; 0.12 is about 83
 * degrees off square, by which point the wall is a line and the pattern is
 * gone. Coverage sits below what the far end of the zoom range produces for a
 * fitted wall, because that zoom is a choice rather than a fault.
 */
export const FRAMING_LOST_VISIBLE = 0.5;
export const FRAMING_FOUND_VISIBLE = 0.65;
export const FRAMING_LOST_COVERAGE = 0.005;
export const FRAMING_FOUND_COVERAGE = 0.01;
export const FRAMING_LOST_FACING = 0.12;
export const FRAMING_FOUND_FACING = 0.2;

/** Fully framed, used when the wall is empty or wraps around the camera. */
const WHOLE: Framing = { visible: 1, coverage: 1, facing: 1 };

const probe = new THREE.Vector3();
const view = new THREE.Vector3();

export function measureFraming(box: THREE.Box3, camera: THREE.PerspectiveCamera): Framing {
  if (box.isEmpty()) return WHOLE;

  camera.updateMatrixWorld();
  const { min, max } = box;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < 8; i += 1) {
    const front = (i & 4) !== 0;
    probe
      .set(i & 1 ? max.x : min.x, i & 2 ? max.y : min.y, front ? max.z : min.z)
      .applyMatrix4(camera.matrixWorldInverse);

    // Past the near plane the perspective divide flips the point, so a box
    // straddling the camera cannot be measured this way. It does not need to
    // be: the camera is inside the wall, which is as framed as it gets.
    if (probe.z > -camera.near) return WHOLE;

    // Only the front face is the design. The back corners are still projected,
    // because a camera between the two planes has to be caught above.
    if (!front) continue;

    probe.applyMatrix4(camera.projectionMatrix);
    minX = Math.min(minX, probe.x);
    maxX = Math.max(maxX, probe.x);
    minY = Math.min(minY, probe.y);
    maxY = Math.max(maxY, probe.y);
  }

  const inside =
    Math.max(0, Math.min(maxX, 1) - Math.max(minX, -1)) *
    Math.max(0, Math.min(maxY, 1) - Math.max(minY, -1));
  const projected = (maxX - minX) * (maxY - minY);

  // Panels lie in the XY plane, so the wall's normal is the z axis and the
  // view direction's z component is already the cosine against it.
  camera.getWorldDirection(view);

  // Normalised device coordinates run from -1 to 1 on each axis, so the
  // viewport's area is four.
  return {
    visible: projected > 0 ? inside / projected : 0,
    coverage: inside / 4,
    facing: Math.abs(view.z),
  };
}

export function isLost(framing: Framing): boolean {
  return (
    framing.visible < FRAMING_LOST_VISIBLE ||
    framing.coverage < FRAMING_LOST_COVERAGE ||
    framing.facing < FRAMING_LOST_FACING
  );
}

export function isFound(framing: Framing): boolean {
  return (
    framing.visible > FRAMING_FOUND_VISIBLE &&
    framing.coverage > FRAMING_FOUND_COVERAGE &&
    framing.facing > FRAMING_FOUND_FACING
  );
}
